import { create } from 'zustand';
import api from '../lib/api';

/* ═══════════════════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════════════════ */

export interface AdminUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  email: string;
  is_admin: boolean;
  admin_role: 'admin' | 'senior_mod' | 'moderator' | null;
  is_banned: boolean;
  is_suspended: boolean;
  account_state: 'good' | 'limited' | 'at_risk';
  created_at: string;
  online?: boolean;
}

export interface AdminReport {
  id: string;
  reporter_id: string;
  reporter_username: string;
  reporter_avatar: string | null;
  reported_user_id: string;
  reported_username: string;
  reported_avatar: string | null;
  target_type: 'message' | 'user' | 'voice';
  target_id: string;
  reason: string;
  details: string | null;
  status: 'pending' | 'reviewed' | 'dismissed';
  admin_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  message_content: string | null;
  channel_id: string | null;
  server_id: string | null;
  server_name: string | null;
  channel_name: string | null;
  created_at: string;
}

export interface AdminAppeal {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  action_id: string;
  action_type: string;
  action_reason: string;
  reason: string;
  status: 'pending' | 'accepted' | 'rejected';
  admin_response: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface AdminServer {
  id: string;
  name: string;
  icon_url: string | null;
  owner_id: string;
  owner_username: string;
  member_count: number;
  is_banned: boolean;
  ban_reason: string | null;
  created_at: string;
}

export interface AdminTransaction {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  type: string;
  status: string;
  amount: number;
  currency: string;
  wallet_amount: number;
  stripe_amount: number;
  item_name: string | null;
  description: string | null;
  created_at: string;
}

export interface AuditLogEntry {
  id: string;
  admin_id: string;
  admin_username: string;
  admin_avatar: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface AdminNote {
  id: string;
  user_id: string;
  author_id: string;
  author_username: string;
  author_avatar: string | null;
  content: string;
  created_at: string;
}

export interface SystemAnnouncement {
  id: string;
  author_id: string;
  author_username: string;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'maintenance' | 'update';
  is_active: boolean;
  starts_at: string;
  ends_at: string | null;
  created_at: string;
}

/* ── Per-user billing types ── */
export interface UserBillingData {
  wallet: { balance: number; currency: string };
  transactions: AdminTransaction[];
  subscriptions: UserSubscription[];
  paymentMethods: UserPaymentMethod[];
}

export interface UserSubscription {
  id: string;
  user_id: string;
  type: string;
  status: string;
  stripe_subscription_id: string;
  stripe_price_id: string;
  hosted_server_id: string | null;
  plan_name: string | null;
  price_monthly: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  created_at: string;
}

export interface UserPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface UserGameEntry {
  id: string;
  user_id: string;
  game_id: string;
  title: string;
  slug: string;
  cover_image_url: string | null;
  price: number;
  sale_price: number | null;
  purchased_at: string;
  play_time_minutes: number;
  last_played_at: string | null;
}

export interface CatalogGame {
  id: string;
  title: string;
  slug: string;
  cover_image_url: string | null;
  price: number;
  sale_price: number | null;
}

export interface SubscriptionPlan {
  id: string;
  type: string;
  name: string;
  display_name: string;
  price_monthly: number;
  currency: string;
  stripe_price_id: string;
}

export interface DashboardStats {
  users: { total: number; online: number; new24h: number; new7d: number; new30d: number; banned: number; suspended: number };
  moderation: { pendingReports: number; pendingAppeals: number; actionsToday: number };
  billing: { totalRevenue: number; revenueToday: number; revenue7d: number; revenue30d: number; totalTransactions: number; totalRefunded: number; activeSubscriptions: number };
  servers: { total: number; banned: number };
  games: { total: number; pendingReview: number };
  recentActivity: Array<{ type: string; description: string; time: string; icon?: string }>;
  alerts: Array<{ level: 'info' | 'warning' | 'critical'; message: string; time: string }>;
}

export type AdminSection =
  | 'dashboard' | 'users' | 'reports' | 'appeals'
  | 'servers' | 'billing' | 'games' | 'minecraft'
  | 'audit' | 'announcements';

const asRecord = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
};

const numberValue = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeDashboardStats = (data: unknown): DashboardStats => {
  const root = asRecord(data);
  const users = asRecord(root.users);
  const moderation = asRecord(root.moderation);
  const billing = asRecord(root.billing);
  const servers = asRecord(root.servers);
  const games = asRecord(root.games);

  return {
    users: {
      total: numberValue(users.total ?? root.total_users),
      online: numberValue(users.online),
      new24h: numberValue(users.new24h),
      new7d: numberValue(users.new7d),
      new30d: numberValue(users.new30d),
      banned: numberValue(users.banned),
      suspended: numberValue(users.suspended),
    },
    moderation: {
      pendingReports: numberValue(moderation.pendingReports ?? root.pending_reports),
      pendingAppeals: numberValue(moderation.pendingAppeals ?? root.pending_appeals),
      actionsToday: numberValue(moderation.actionsToday ?? root.active_mod_actions),
    },
    billing: {
      totalRevenue: numberValue(billing.totalRevenue),
      revenueToday: numberValue(billing.revenueToday),
      revenue7d: numberValue(billing.revenue7d),
      revenue30d: numberValue(billing.revenue30d),
      totalTransactions: numberValue(billing.totalTransactions),
      totalRefunded: numberValue(billing.totalRefunded),
      activeSubscriptions: numberValue(billing.activeSubscriptions),
    },
    servers: {
      total: numberValue(servers.total ?? root.total_servers),
      banned: numberValue(servers.banned),
    },
    games: {
      total: numberValue(games.total),
      pendingReview: numberValue(games.pendingReview),
    },
    recentActivity: Array.isArray(root.recentActivity) ? root.recentActivity as DashboardStats['recentActivity'] : [],
    alerts: Array.isArray(root.alerts) ? root.alerts as DashboardStats['alerts'] : [],
  };
};

/* ═══════════════════════════════════════════════════════════════════════════
   Store
   ═══════════════════════════════════════════════════════════════════════════ */

interface AdminState {
  isAdminConnectActive: boolean;
  currentSection: AdminSection;
  setAdminConnectActive: (v: boolean) => void;
  setSection: (s: AdminSection) => void;

  // Dashboard
  dashboardStats: DashboardStats | null;
  isLoadingDashboard: boolean;
  fetchDashboard: () => Promise<void>;

  // Users
  users: AdminUser[];
  usersTotal: number;
  isLoadingUsers: boolean;
  fetchUsers: (search?: string) => Promise<void>;
  selectedUserId: string | null;
  setSelectedUserId: (id: string | null) => void;

  // Reports
  reports: AdminReport[];
  reportsTotal: number;
  isLoadingReports: boolean;
  fetchReports: (status?: string) => Promise<void>;
  updateReport: (id: string, status: string, adminNote?: string) => Promise<boolean>;

  // Appeals
  appeals: AdminAppeal[];
  appealsTotal: number;
  isLoadingAppeals: boolean;
  fetchAppeals: (status?: string) => Promise<void>;
  reviewAppeal: (id: string, status: string, response?: string) => Promise<boolean>;

  // Servers
  servers: AdminServer[];
  serversTotal: number;
  isLoadingServers: boolean;
  fetchServers: (search?: string) => Promise<void>;
  banServer: (id: string, reason: string, notify: boolean) => Promise<boolean>;
  unbanServer: (id: string) => Promise<boolean>;

  // Billing
  transactions: AdminTransaction[];
  transactionsTotal: number;
  revenueStats: { total_revenue: number; total_refunded: number; total_transactions: number } | null;
  isLoadingTransactions: boolean;
  fetchTransactions: (offset?: number, type?: string) => Promise<void>;
  refundTransaction: (txId: string) => Promise<boolean>;
  creditWallet: (userId: string, amount: number, reason: string) => Promise<boolean>;

  // Per-user billing
  userBilling: UserBillingData | null;
  isLoadingUserBilling: boolean;
  fetchUserBilling: (userId: string) => Promise<void>;
  refundUserTransaction: (userId: string, txId: string, reason: string) => Promise<boolean>;
  detachPaymentMethod: (userId: string, pmId: string) => Promise<boolean>;
  cancelSubscription: (userId: string, subId: string) => Promise<boolean>;
  changeSubscriptionPlan: (userId: string, subId: string, newPriceId: string) => Promise<boolean>;

  // Per-user games
  userGames: UserGameEntry[];
  isLoadingUserGames: boolean;
  gameCatalog: CatalogGame[];
  subscriptionPlans: SubscriptionPlan[];
  fetchUserGames: (userId: string) => Promise<void>;
  fetchGameCatalog: () => Promise<void>;
  fetchSubscriptionPlans: () => Promise<void>;
  grantGame: (userId: string, gameId: string, reason: string) => Promise<boolean>;
  revokeGame: (userId: string, gameId: string, reason: string) => Promise<boolean>;
  revokeRefundGame: (userId: string, gameId: string, reason: string) => Promise<boolean>;

  // Audit log
  auditEntries: AuditLogEntry[];
  auditTotal: number;
  isLoadingAudit: boolean;
  fetchAuditLog: (offset?: number, action?: string) => Promise<void>;

  // Announcements
  announcements: SystemAnnouncement[];
  isLoadingAnnouncements: boolean;
  fetchAnnouncements: () => Promise<void>;
  createAnnouncement: (data: { title: string; content: string; type: string; endsAt?: string }) => Promise<boolean>;
  toggleAnnouncement: (id: string, active: boolean) => Promise<boolean>;
  deleteAnnouncement: (id: string) => Promise<boolean>;

  // Notes
  userNotes: AdminNote[];
  isLoadingNotes: boolean;
  fetchUserNotes: (userId: string) => Promise<void>;
  addNote: (userId: string, content: string) => Promise<boolean>;
  deleteNote: (noteId: string) => Promise<boolean>;

  // Moderation actions
  addModerationAction: (userId: string, data: { actionType: string; reason: string; subType?: string; duration?: number }) => Promise<boolean>;
  revokeModeration: (actionId: string) => Promise<boolean>;

  // Admin role management
  setAdminRole: (userId: string, role: string | null) => Promise<boolean>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  isAdminConnectActive: false,
  currentSection: 'dashboard',
  setAdminConnectActive: (v) => set({ isAdminConnectActive: v }),
  setSection: (s) => set({ currentSection: s }),

  /* ── Dashboard ── */
  dashboardStats: null,
  isLoadingDashboard: false,
  fetchDashboard: async () => {
    set({ isLoadingDashboard: true });
    try {
      const { data } = await api.get('/admin/dashboard');
      set({ dashboardStats: normalizeDashboardStats(data) });
    } catch { /* silent */ } finally {
      set({ isLoadingDashboard: false });
    }
  },

  /* ── Users ── */
  users: [],
  usersTotal: 0,
  isLoadingUsers: false,
  selectedUserId: null,
  setSelectedUserId: (id) => set({ selectedUserId: id }),
  fetchUsers: async (search) => {
    set({ isLoadingUsers: true });
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const { data } = await api.get(`/admin/users${params}`);
      set({ users: data.users || data, usersTotal: data.total || data.length });
    } catch { /* silent */ } finally {
      set({ isLoadingUsers: false });
    }
  },

  /* ── Reports ── */
  reports: [],
  reportsTotal: 0,
  isLoadingReports: false,
  fetchReports: async (status) => {
    set({ isLoadingReports: true });
    try {
      const params = status ? `?status=${status}` : '';
      const { data } = await api.get(`/admin/reports${params}`);
      set({ reports: data.reports || data, reportsTotal: data.total || data.length });
    } catch { /* silent */ } finally {
      set({ isLoadingReports: false });
    }
  },
  updateReport: async (id, status, adminNote) => {
    try {
      await api.put(`/admin/reports/${id}`, { status, admin_note: adminNote });
      get().fetchReports();
      return true;
    } catch { return false; }
  },

  /* ── Appeals ── */
  appeals: [],
  appealsTotal: 0,
  isLoadingAppeals: false,
  fetchAppeals: async (status) => {
    set({ isLoadingAppeals: true });
    try {
      const params = status ? `?status=${status}` : '';
      const { data } = await api.get(`/admin/appeals${params}`);
      set({ appeals: data.appeals || data, appealsTotal: data.total || data.length });
    } catch { /* silent */ } finally {
      set({ isLoadingAppeals: false });
    }
  },
  reviewAppeal: async (id, status, response) => {
    try {
      await api.put(`/admin/appeals/${id}`, { status, admin_response: response });
      get().fetchAppeals();
      return true;
    } catch { return false; }
  },

  /* ── Servers ── */
  servers: [],
  serversTotal: 0,
  isLoadingServers: false,
  fetchServers: async (search) => {
    set({ isLoadingServers: true });
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const { data } = await api.get(`/admin/servers${params}`);
      set({ servers: data.servers || data, serversTotal: data.total || data.length });
    } catch { /* silent */ } finally {
      set({ isLoadingServers: false });
    }
  },
  banServer: async (id, reason, notify) => {
    try {
      await api.post(`/admin/servers/${id}/ban`, { reason, notifyMembers: notify });
      get().fetchServers();
      return true;
    } catch { return false; }
  },
  unbanServer: async (id) => {
    try {
      await api.delete(`/admin/servers/${id}/ban`);
      get().fetchServers();
      return true;
    } catch { return false; }
  },

  /* ── Billing ── */
  transactions: [],
  transactionsTotal: 0,
  revenueStats: null,
  isLoadingTransactions: false,
  fetchTransactions: async (offset = 0, type) => {
    set({ isLoadingTransactions: true });
    try {
      const params = new URLSearchParams({ offset: String(offset), limit: '50' });
      if (type) params.set('type', type);
      const { data } = await api.get(`/admin/billing/transactions?${params}`);
      const nextTransactions = Array.isArray(data) ? data : data.transactions || [];
      set({
        transactions: offset === 0 ? nextTransactions : [...get().transactions, ...nextTransactions],
        transactionsTotal: data.total ?? nextTransactions.length,
        revenueStats: data.revenue ?? null,
      });
    } catch { /* silent */ } finally {
      set({ isLoadingTransactions: false });
    }
  },
  refundTransaction: async (txId) => {
    try {
      await api.post(`/admin/billing/transactions/${txId}/refund`);
      get().fetchTransactions();
      return true;
    } catch { return false; }
  },
  creditWallet: async (userId, amount, reason) => {
    try {
      await api.post(`/admin/users/${userId}/wallet-credit`, { amount, reason });
      return true;
    } catch { return false; }
  },

  /* ── Per-user Billing ── */
  userBilling: null,
  isLoadingUserBilling: false,
  fetchUserBilling: async (userId) => {
    set({ isLoadingUserBilling: true });
    try {
      const { data } = await api.get(`/admin/users/${userId}/billing`);
      set({ userBilling: data });
    } catch { /* silent */ } finally {
      set({ isLoadingUserBilling: false });
    }
  },
  refundUserTransaction: async (userId, txId, reason) => {
    try {
      await api.post(`/admin/users/${userId}/transactions/${txId}/refund`, { reason });
      get().fetchUserBilling(userId);
      return true;
    } catch { return false; }
  },
  detachPaymentMethod: async (userId, pmId) => {
    try {
      await api.delete(`/admin/users/${userId}/payment-methods/${pmId}`);
      get().fetchUserBilling(userId);
      return true;
    } catch { return false; }
  },
  cancelSubscription: async (userId, subId) => {
    try {
      await api.post(`/admin/users/${userId}/subscriptions/${subId}/cancel`);
      get().fetchUserBilling(userId);
      return true;
    } catch { return false; }
  },
  changeSubscriptionPlan: async (userId, subId, newPriceId) => {
    try {
      await api.post(`/admin/users/${userId}/subscriptions/${subId}/change-plan`, { newPriceId });
      get().fetchUserBilling(userId);
      return true;
    } catch { return false; }
  },

  /* ── Per-user Games ── */
  userGames: [],
  isLoadingUserGames: false,
  gameCatalog: [],
  subscriptionPlans: [],
  fetchUserGames: async (userId) => {
    set({ isLoadingUserGames: true });
    try {
      const { data } = await api.get(`/admin/users/${userId}/games`);
      set({ userGames: data });
    } catch { /* silent */ } finally {
      set({ isLoadingUserGames: false });
    }
  },
  fetchGameCatalog: async () => {
    try {
      const { data } = await api.get('/admin/games/catalog');
      set({ gameCatalog: data });
    } catch { /* silent */ }
  },
  fetchSubscriptionPlans: async () => {
    try {
      const { data } = await api.get('/admin/subscription-plans');
      set({ subscriptionPlans: data });
    } catch { /* silent */ }
  },
  grantGame: async (userId, gameId, reason) => {
    try {
      await api.post(`/admin/users/${userId}/games/grant`, { gameId, reason });
      get().fetchUserGames(userId);
      return true;
    } catch { return false; }
  },
  revokeGame: async (userId, gameId, reason) => {
    try {
      await api.post(`/admin/users/${userId}/games/${encodeURIComponent(gameId)}/revoke`, { reason });
      get().fetchUserGames(userId);
      return true;
    } catch { return false; }
  },
  revokeRefundGame: async (userId, gameId, reason) => {
    try {
      await api.post(`/admin/users/${userId}/games/${encodeURIComponent(gameId)}/revoke-refund`, { reason });
      get().fetchUserGames(userId);
      return true;
    } catch { return false; }
  },

  /* ── Audit Log ── */
  auditEntries: [],
  auditTotal: 0,
  isLoadingAudit: false,
  fetchAuditLog: async (offset = 0, action) => {
    set({ isLoadingAudit: true });
    try {
      const params = new URLSearchParams({ offset: String(offset), limit: '50' });
      if (action) params.set('action', action);
      const { data } = await api.get(`/admin/audit-log?${params}`);
      const nextEntries = Array.isArray(data) ? data : data.entries || [];
      set({
        auditEntries: offset === 0 ? nextEntries : [...get().auditEntries, ...nextEntries],
        auditTotal: data.total ?? nextEntries.length,
      });
    } catch { /* silent */ } finally {
      set({ isLoadingAudit: false });
    }
  },

  /* ── Announcements ── */
  announcements: [],
  isLoadingAnnouncements: false,
  fetchAnnouncements: async () => {
    set({ isLoadingAnnouncements: true });
    try {
      const { data } = await api.get('/admin/announcements');
      set({ announcements: data.announcements || data });
    } catch { /* silent */ } finally {
      set({ isLoadingAnnouncements: false });
    }
  },
  createAnnouncement: async (d) => {
    try {
      await api.post('/admin/announcements', d);
      get().fetchAnnouncements();
      return true;
    } catch { return false; }
  },
  toggleAnnouncement: async (id, active) => {
    try {
      await api.put(`/admin/announcements/${id}`, { is_active: active });
      get().fetchAnnouncements();
      return true;
    } catch { return false; }
  },
  deleteAnnouncement: async (id) => {
    try {
      await api.delete(`/admin/announcements/${id}`);
      get().fetchAnnouncements();
      return true;
    } catch { return false; }
  },

  /* ── Notes ── */
  userNotes: [],
  isLoadingNotes: false,
  fetchUserNotes: async (userId) => {
    set({ isLoadingNotes: true });
    try {
      const { data } = await api.get(`/admin/users/${userId}/notes`);
      set({ userNotes: data.notes || data });
    } catch { /* silent */ } finally {
      set({ isLoadingNotes: false });
    }
  },
  addNote: async (userId, content) => {
    try {
      await api.post(`/admin/users/${userId}/notes`, { content });
      get().fetchUserNotes(userId);
      return true;
    } catch { return false; }
  },
  deleteNote: async (noteId) => {
    try {
      await api.delete(`/admin/notes/${noteId}`);
      set({ userNotes: get().userNotes.filter((note) => note.id !== noteId) });
      return true;
    } catch { return false; }
  },

  /* ── Moderation Actions ── */
  addModerationAction: async (userId, data) => {
    try {
      await api.post(`/admin/users/${userId}/moderation`, data);
      return true;
    } catch { return false; }
  },
  revokeModeration: async (actionId) => {
    try {
      await api.delete(`/admin/moderation/${actionId}`);
      return true;
    } catch { return false; }
  },

  /* ── Admin Role Management ── */
  setAdminRole: async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      get().fetchUsers();
      return true;
    } catch { return false; }
  },
}));
